// Small building blocks shared by the admin pages, so every list looks and
// behaves the same (loading, empty, error, paging).
import React, { useEffect, useState } from "react";
import { Header } from "../../components/page-header/Header";
import Loader from "../../components/loaders/loader";
import { EmptyState } from "../../components/data-table/DataTable";

export const formatDate = (value, withTime = false) => {
  if (!value) return "—";
  const date = new Date(value);
  return withTime
    ? date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : date.toLocaleDateString(undefined, { dateStyle: "medium" });
};

export const PageShell = ({ title, crumbs = [], actions, children }) => (
  <section className="min-h-[86vh] bg-gray-50">
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Header
          title={title}
          breadcrumbItems={[{ label: "Admin", to: "/admin-dashboard" }, ...crumbs, { label: title }]}
        />
        {actions}
      </div>
      {children}
    </div>
  </section>
);

export const Card = ({ title, action, children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-2 px-5 pt-4">
        {title && <h2 className="text-base font-semibold text-slate-800">{title}</h2>}
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

const PILL_TONES = {
  green: "bg-green-50 text-green-700 ring-green-600/20",
  yellow: "bg-yellow-50 text-yellow-800 ring-yellow-600/20",
  red: "bg-red-50 text-red-700 ring-red-600/20",
  blue: "bg-blue-50 text-blue-700 ring-blue-600/20",
  gray: "bg-gray-50 text-gray-600 ring-gray-500/20",
  purple: "bg-purple-50 text-purple-700 ring-purple-600/20",
};

export const Pill = ({ tone = "gray", children }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${PILL_TONES[tone]}`}>
    {children}
  </span>
);

const VERIFICATION_TONES = { verified: "green", submitted: "blue", pending: "yellow", rejected: "red" };
const VERIFICATION_LABELS = { verified: "Verified", submitted: "Needs review", pending: "Not submitted", rejected: "Rejected" };
export const VerificationPill = ({ status }) => (
  <Pill tone={VERIFICATION_TONES[status] || "gray"}>{VERIFICATION_LABELS[status] || status || "—"}</Pill>
);

const TICKET_TONES = { open: "yellow", in_progress: "blue", resolved: "green", closed: "gray" };
export const TICKET_STATUS_LABELS = { open: "Open", in_progress: "In progress", resolved: "Resolved", closed: "Closed" };
export const TicketStatusPill = ({ status }) => (
  <Pill tone={TICKET_TONES[status] || "gray"}>{TICKET_STATUS_LABELS[status] || status}</Pill>
);

export const PlanPill = ({ plan }) => (
  <Pill tone={plan && plan !== "free" ? "purple" : "gray"}>{plan ? plan[0].toUpperCase() + plan.slice(1) : "Free"}</Pill>
);

// Search box that only reports a value once typing pauses.
export const SearchBox = ({ value, onChange, placeholder = "Search…" }) => {
  const [text, setText] = useState(value || "");
  useEffect(() => {
    const timer = setTimeout(() => text !== value && onChange(text), 350);
    return () => clearTimeout(timer);
  }, [text, value, onChange]);
  return (
    <input
      type="search"
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      className="w-full sm:w-72 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
    />
  );
};

export const Select = ({ label, value, onChange, options }) => (
  <select
    aria-label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
  >
    {options.map(([optionValue, optionLabel]) => (
      <option key={optionValue} value={optionValue}>{optionLabel}</option>
    ))}
  </select>
);

const errorMessage = (error) => error?.data?.message || error?.error || "Something went wrong";

// columns: [{ key, label, render?(row), className? }]
export const DataTable = ({ columns, rows, isLoading, error, emptyText = "Nothing here yet", onRowClick, rowLabel }) => {
  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader /></div>;
  }
  if (error) {
    return <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">{errorMessage(error)}</div>;
  }
  if (!rows?.length) {
    return <div className="rounded-xl border border-gray-200 bg-white"><EmptyState title={emptyText} /></div>;
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* mh-table: rows become labelled cards on phones (index.css). */}
      <table className="mh-table min-w-full text-sm">
        <thead className="border-b border-gray-200 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col" className={`px-4 py-3 ${col.className || ""}`}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr
              key={row._id}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? (e) => e.key === "Enter" && onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              aria-label={onRowClick && rowLabel ? rowLabel(row) : undefined}
              className={onRowClick ? "cursor-pointer hover:bg-gray-50 focus:bg-gray-50 focus:outline-none" : ""}
            >
              {columns.map((col) => (
                <td key={col.key} data-label={col.label || ""} className={`px-4 py-3 text-gray-700 ${col.className || ""}`}>
                  {col.render ? col.render(row) : row[col.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const Pager = ({ pagination, onPage }) => {
  if (!pagination || pagination.totalPages <= 1) {
    return pagination ? <p className="mt-3 text-xs text-gray-500">{pagination.total} total</p> : null;
  }
  const { page, totalPages, total } = pagination;
  const button = "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm disabled:opacity-40";
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
      <span>{total} total · page {page} of {totalPages}</span>
      <div className="flex gap-2">
        <button type="button" className={button} disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
        <button type="button" className={button} disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Next</button>
      </div>
    </div>
  );
};

export const siteUrl = (path) => (path ? `https://masjidhub.org/sites/${path}` : null);

export const ConfirmDialog = ({ isOpen, title, description, confirmLabel, danger, isLoading, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-md rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
          {description && <p className="mt-2 text-sm text-gray-600">{description}</p>}
        </div>
        <div className="flex justify-end gap-2 rounded-b-xl bg-gray-50 px-6 py-3">
          <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            disabled={isLoading}
            onClick={onConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${danger ? "bg-red-600 hover:bg-red-700" : "bg-accent hover:bg-blue-700"}`}
          >
            {isLoading ? "Saving…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
